import { z } from 'zod'

import type { AgentforceSessionData } from '@/composables/chat/providers/agentforce/types'

function firstZodMessage (error: z.ZodError): string {
  return error.issues[0]?.message ?? 'Invalid Agentforce session data'
}

/** Treats non-strings as empty so `.min(1)` yields the given message (not Zod's default "expected string"). */
function requiredTrimmedString (message: string) {
  return z.preprocess(
    (v) => (typeof v === 'string' ? v.trim() : ''),
    z.string().min(1, { message })
  )
}

/**
 * Core `salesforce_in_app_chat_jwt` JSON body: plain object with known snake_case keys.
 * Frontdesk reads `url_web` and `url_mobile` (core `url` is ignored — used by other apps).
 * Extra keys are ignored after normalization.
 */
export const agentforceJwtApiRecordSchema = z
  .record(z.string(), z.unknown())
  .transform(
    (raw): AgentforceSessionData => {
      const jwtFromJwt = typeof raw.jwt === 'string' ? raw.jwt.trim() : ''
      const jwtFromToken = typeof raw.token === 'string' ? raw.token.trim() : ''
      const jwt = jwtFromJwt || jwtFromToken
      const conversationId = typeof raw.conversation_id === 'string' ? raw.conversation_id.trim() : ''
      const organizationId = typeof raw.organization_id === 'string'
        ? raw.organization_id.trim() || undefined
        : undefined
      const urlWebRaw = typeof raw.url_web === 'string' ? raw.url_web.trim().replace(/\/+$/, '') : ''
      const urlMobileRaw = typeof raw.url_mobile === 'string' ? raw.url_mobile.trim().replace(/\/+$/, '') : ''
      const developerNameWeb = typeof raw.developer_name_web === 'string' ? raw.developer_name_web.trim() : ''

      /** ESW `scrt2URL`: prefer core `url_mobile`, else same base as web (`url_web`). */
      const scrtBase = urlMobileRaw || urlWebRaw || undefined

      return {
        jwt,
        conversation_id: conversationId,
        organizationId,
        scrtBaseUrl: scrtBase,
        embedded_messaging_site_url: urlWebRaw || undefined,
        embedded_messaging_deployment_name: developerNameWeb || undefined,
      }
    }
  )

/** Maps core JWT body into {@link AgentforceSessionData} (permissive; required fields checked separately). */
export function normalizeAgentforceJwtPayload (raw: unknown): AgentforceSessionData {
  const result = agentforceJwtApiRecordSchema.safeParse(raw)
  if (!result.success) {
    throw new Error('Agentforce JWT endpoint returned non-object body')
  }
  return result.data
}

const agentforceJwtAndConversationSchema = z.object({
  jwt: requiredTrimmedString(
    'Agentforce JWT missing or empty (expected `token` or `jwt` from salesforce_in_app_chat_jwt)'
  ),
  conversation_id: requiredTrimmedString('Agentforce response missing conversation_id'),
})

export function assertValidAgentforcePayload (data: AgentforceSessionData): void {
  const result = agentforceJwtAndConversationSchema.safeParse({
    jwt: data.jwt,
    conversation_id: data.conversation_id,
  })
  if (!result.success) {
    throw new Error(firstZodMessage(result.error))
  }
}

/**
 * Core JWT must include organization_id, `url_web`, and developer_name_web. Frontdesk ignores core `url`
 * (other clients). `url_web` is the Experience Cloud site; `scrt2URL` prefers `url_mobile` when present.
 */
export const agentforceEmbedReadySessionSchema = z.looseObject({
  jwt: requiredTrimmedString('Agentforce session JWT is missing'),
  conversation_id: requiredTrimmedString('Agentforce response missing conversation_id'),
  organizationId: requiredTrimmedString('Agentforce response missing organization_id'),
  scrtBaseUrl: requiredTrimmedString(
    'Agentforce response missing url_mobile or url_web (used as scrt2URL)'
  ),
  embedded_messaging_deployment_name: requiredTrimmedString(
    'Agentforce response missing developer_name_web'
  ),
  embedded_messaging_site_url: requiredTrimmedString(
    'Agentforce response missing url_web (Embedded Messaging site base URL)'
  ),
})

export function assertAgentforceSessionFromApi (data: AgentforceSessionData): void {
  const result = agentforceEmbedReadySessionSchema.safeParse(data)
  if (!result.success) {
    throw new Error(firstZodMessage(result.error))
  }
}
